import React, { useEffect, useId, useRef, useState } from 'react';

const DEFAULT_ON_CHANGE_DEBOUNCE_MS = 300;

const CkTextEditorComponent = ({
    id: idProp,
    type = 'email',
    input = '',
    output,
    onChange,
    readOnly = false,
    editorOptions,
    debounce = DEFAULT_ON_CHANGE_DEBOUNCE_MS,
    // When true, onChange is only fired after the user has actually edited the content.
    // CKEditor fires a `change` event while settling the initial programmatically-set data
    // (and the normalized getData() can differ from `input`), which would otherwise look
    // like a user edit and e.g. mark a form dirty on load. Opt-in to keep existing
    // consumers' behavior unchanged.
    notifyOnUserChangeOnly = false,
    ...restProps
}) => {
    const editorId = idProp ?? `ck-editor-${useId().replace(/:/g, '')}`;
    const unmountedRef = useRef(false);
    const onChangeDebounceRef = useRef(null);
    const userInteractedRef = useRef(false);
    const [instance, setInstance] = useState(null);
    const [initialized, setInitialized] = useState(false);

    useEffect(() => {
        if (initialized) return;
        unmountedRef.current = false;
        replaceWithCKEditor(editorId, type, editorOptions);
        const editor = CKEDITOR.instances[editorId];
        if (!editor) return undefined;

        const finishInit = () => {
            if (unmountedRef.current) return;
            setInstance(editor);
            editor.setReadOnly(readOnly);
            editor.setData(typeof input === 'string' ? input : '');
            setInitialized(true);
        };
        if (editor.status === 'ready') finishInit();
        else editor.once('instanceReady', finishInit);

        return () => {
            unmountedRef.current = true;
            const inst = instance || CKEDITOR.instances[editorId];
            if (inst && !inst.isDestroyed()) inst.destroy();
            setInstance(null);
            setInitialized(false);
        };
    }, [editorId, type]);

    useEffect(() => {
        const inst = getInstance();
        if (inst && !inst.isDestroyed() && initialized) inst.setReadOnly(readOnly);
    }, [readOnly, initialized]);

    const getInstance = () => {
        return instance || CKEDITOR.instances[editorId];
    };

    useEffect(() => {
        const inst = getInstance();
        if (!inst || inst.isDestroyed() || !initialized) return undefined;

        const clearDebounce = () => {
            if (!onChangeDebounceRef.current) return false;
            clearTimeout(onChangeDebounceRef.current);
            onChangeDebounceRef.current = null;
            return true;
        };

        // A change is the user's only if they have actually interacted with the editor;
        // changes that fire while settling the initial/programmatic data are not.
        const shouldNotifyChange = () => !notifyOnUserChangeOnly || userInteractedRef.current;
        const markInteracted = () => { userInteractedRef.current = true; };

        const onBlur = () => {
            const hadPending = clearDebounce();
            if (unmountedRef.current) return;
            if (hadPending && shouldNotifyChange()) onChange?.(inst.getData());
            output?.(inst.getData());
        };

        const scheduleNotify = (force) => {
            clearDebounce();
            onChangeDebounceRef.current = setTimeout(() => {
                onChangeDebounceRef.current = null;
                if (!unmountedRef.current && (force || shouldNotifyChange())) onChange?.(inst.getData());
            }, debounce);
        };
        const handleChange = () => scheduleNotify(false);

        // AI-assistant output is programmatic but user-visible: notify so the generated text
        // reaches the consumer's form state, without latching the user-interaction flag (which
        // would defeat notifyOnUserChangeOnly's suppression of later programmatic writes).
        const onAiWrite = () => scheduleNotify(true);

        inst.on('blur', onBlur);
        inst.on('change', handleChange);
        if (notifyOnUserChangeOnly) {
            // User-edit vectors: typing, paste, drag-drop, and toolbar commands (bold, etc.).
            inst.on('key', markInteracted);
            inst.on('paste', markInteracted);
            inst.on('drop', markInteracted);
            inst.on('afterCommandExec', markInteracted);
            inst.on('aiAssistantWrite', onAiWrite);
        }

        return () => {
            const hadPending = clearDebounce();
            const inst = getInstance();
            if (hadPending && inst && !unmountedRef.current && !inst.isDestroyed() && shouldNotifyChange()) {
                onChange?.(inst.getData());
            }
            if (inst && !inst.isDestroyed()) {
                inst.removeListener('blur', onBlur);
                inst.removeListener('change', handleChange);
                if (notifyOnUserChangeOnly) {
                    inst.removeListener('key', markInteracted);
                    inst.removeListener('paste', markInteracted);
                    inst.removeListener('drop', markInteracted);
                    inst.removeListener('afterCommandExec', markInteracted);
                    inst.removeListener('aiAssistantWrite', onAiWrite);
                }
            }
        };
    }, [initialized, debounce, onChange, output, notifyOnUserChangeOnly]);

    useEffect(() => {
        const inst = getInstance();
        if (!inst || inst.isDestroyed() || !initialized) return;
        if (typeof input !== 'string' || inst.getData() === input) return;
        inst.setData(input);
    }, [input, initialized]);

    return <span {...restProps} id={editorId} />;
};

export { CkTextEditorComponent };
window.CkTextEditorComponent = CkTextEditorComponent;
export default CkTextEditorComponent;
